/**
 * Zod schema for the contact edit form (React Hook Form + zodResolver).
 */

import { z } from 'zod';
import {
  ACTION_STATE_LABELS,
  STATUS_LABELS,
  type ActionState,
  type Contact,
  type ContactStatus,
} from './types.js';

// Derived from the domain label maps rather than re-listed, so the form
// can never drift from the enums defined in `src/lib/crm/types.ts`.
const STATUS_VALUES = Object.keys(STATUS_LABELS) as [ContactStatus, ...ContactStatus[]];
const ACTION_STATE_VALUES = Object.keys(ACTION_STATE_LABELS) as [ActionState, ...ActionState[]];

const DUE_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Sentinel value for the "not set" option in the action_state select. An
// empty string cannot collide with a real ActionState value.
const ACTION_STATE_NOT_SET = '';

// Clearing a free-text field is a real (if implicit) user action, so a
// blank field must resolve to `null`, never to `''`.
const trimmedNullableText = z.string().transform((value): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
});

// Clearing the due date is a real user action, and writing `''` into a
// `date` column fails at the database, so blank must become `null` too.
const dueDateField = z
  .string()
  .transform((value): string | null => {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  })
  .refine((value) => value === null || DUE_DATE_PATTERN.test(value), {
    message: 'Due date must be in YYYY-MM-DD format.',
  });

// action_state needs an explicit "not set" choice (distinct from every real
// ActionState) because clearing it moves a contact between dashboard lanes —
// it must map to `null`, not be silently coerced or left unrepresentable.
const actionStateField = z
  .union([z.enum(ACTION_STATE_VALUES), z.literal(ACTION_STATE_NOT_SET)])
  .transform((value): ActionState | null =>
    (value === ACTION_STATE_NOT_SET ? null : value));

export const contactEditSchema = z.object({
  status: z.enum(STATUS_VALUES),
  action_state: actionStateField,
  due_date: dueDateField,
  next_action: trimmedNullableText,
  pain_point: trimmedNullableText,
  strategy: trimmedNullableText,
  notes: trimmedNullableText,
});

export type ContactEditValues = z.infer<typeof contactEditSchema>;

export const contactToEditValues = (contact: Contact): ContactEditValues => ({
  status: contact.status,
  action_state: contact.action_state ?? null,
  due_date: contact.due_date ?? null,
  next_action: contact.next_action ?? null,
  pain_point: contact.pain_point ?? null,
  strategy: contact.strategy ?? null,
  notes: contact.notes ?? null,
});

// `Contact`'s optional fields are modelled as `string | undefined` (there is
// no `| null` in the domain type), but clearing a field requires sending an
// explicit `null` to Supabase — omitting the key would leave the column
// untouched instead of clearing it. The cast below is the single boundary
// point where that DB-level nullability meets the domain type; every field
// assigned here is one `crmApi.updateContact` already accepts and forwards
// as-is (see `ALLOWED_CONTACT_FIELDS` in `src/lib/crm/api.ts`).
export const editValuesToUpdate = (values: ContactEditValues): Partial<Contact> => {
  const patch = {
    status: values.status,
    action_state: values.action_state,
    due_date: values.due_date,
    next_action: values.next_action,
    pain_point: values.pain_point,
    strategy: values.strategy,
    notes: values.notes,
  };

  return patch as Partial<Contact>;
};
